import { koloryWypowiedzi } from "@shared/koloryWypowiedzi.js";
import { numerWypowiedzi } from "@shared/numeracja.js";
import { STATUS_CZEKA_NA_CZLOWIEKA, type FindingKind } from "@shared/ocenaTypy.js";
import {
  rozstrzygnijNakladanie,
  zakresWskazania,
  type ZakresCytatu,
} from "../components/transcript/fragmenty.js";
import type { LensFindingDto, LensRunDto, SessionDetail, UtteranceDto, WariantWskazaniaDto } from "./api.js";
import type { JakoscWypowiedzi } from "./jakoscWypowiedzi.js";
import { IN_PROGRESS } from "./sessionStatus.js";

/**
 * Raport z oceny wg metodyki jako dane: czyste funkcje bez DOM i bez Reacta.
 *
 * Karta oceny i transkrypt tylko RYSUJA to, co tu policzono. Wszystkie rozstrzygniecia, ktore
 * latwo zepsuc (czy brak wskazan to brak oceny, ktore cytaty sa w transkrypcie, komu naleza wskazania,
 * ktore cwiczenia sie naleza), siedza w tym jednym pliku i maja testy bez przegladarki.
 *
 * Zrodlem jest wylacznie odpowiedz GET /api/sessions/:id: `lensRuns` to NAJNOWSZY przebieg kazdej soczewki
 * z ostatniego zadania oceny, a `ocena.soczewki` to wybor tego zadania, wiec raport pokazuje tylko wybrane
 * parametry. W interfejsie mowimy "parametry"; "soczewka" to wewnetrzna nazwa metodyki.
 */

// --- Stany parametrow i karty --------------------------------------------------------------------

/**
 * Co wiemy o jednym wybranym parametrze. To rozroznienie jest sensem karty: "oceniono, brak wskazan"
 * jest uczciwym wynikiem (np. rozmowa grzecznosciowa), a "nie policzona" albo "ucieta" znaczy, ze wyniku
 * nie ma - i nigdy nie wolno ich pokazac tak samo.
 */
export type StanParametru =
  | { rodzaj: "oceniono"; liczbaWskazan: number }
  | { rodzaj: "brak-wskazan" }
  /** Przebieg zapisany, ale nie OK: TRUNCATED (ucieta), SCHEMA_INVALID (niezgodna), BLOCKED, ERROR. */
  | { rodzaj: "nieudana"; status: string; etykieta: string; opis: string; szczegol: string | null }
  | { rodzaj: "w-toku" }
  /** Zadanie sie skonczylo, a przebiegu nie ma (porazka zadania albo awaria zapisu). */
  | { rodzaj: "niepoliczona" };

export interface ParametrWidoku {
  slug: string;
  /** Tytul po polsku, w wersji metodyki, ktora policzyla przebieg (a bez przebiegu: z wyboru zadania). */
  tytul: string;
  stan: StanParametru;
  przebieg: LensRunDto | null;
}

/** Krotka etykieta (plakietka) i zdanie dla przebiegu, ktory sie nie powiodl. S-1: po ludzku, bez kodow. */
const NIEUDANE: Readonly<Record<string, { etykieta: string; opis: string }>> = {
  TRUNCATED: {
    etykieta: "Ocena ucięta",
    opis: "Odpowiedź modelu urwała się, zanim skończył, więc tego parametru nie oceniono do końca.",
  },
  SCHEMA_INVALID: {
    etykieta: "Ocena niezgodna",
    opis: "Odpowiedź modelu nie przeszła kontroli, więc żadne jej wskazanie nie trafiło do raportu.",
  },
  BLOCKED: {
    etykieta: "Model odmówił",
    opis: "Model odmówił oceny tego parametru.",
  },
  ERROR: {
    etykieta: "Ocena nieudana",
    opis: "Nie udało się uzyskać oceny od modelu.",
  },
};

function opisNieudanego(status: string): { etykieta: string; opis: string } {
  return NIEUDANE[status] ?? { etykieta: "Ocena nieudana", opis: `Ocena tego parametru nie powiodła się (${status}).` };
}

export function stanParametru(przebieg: LensRunDto | null, trwa: boolean): StanParametru {
  if (przebieg === null) return trwa ? { rodzaj: "w-toku" } : { rodzaj: "niepoliczona" };
  if (przebieg.status === "OK") {
    return przebieg.findings.length > 0
      ? { rodzaj: "oceniono", liczbaWskazan: przebieg.findings.length }
      : { rodzaj: "brak-wskazan" };
  }
  return { rodzaj: "nieudana", status: przebieg.status, ...opisNieudanego(przebieg.status), szczegol: przebieg.error };
}

/**
 * Co pokazuje karta:
 *  - czeka-na-podzial: tekst bez podpisow czeka na zatwierdzenie podzialu, ocena jeszcze nie ruszyla,
 *  - czeka-na-etapy: nie ma jeszcze transkryptu (nagranie w obrobce, tekst w podziale),
 *  - brak-wypowiedzi: transkryptu nie bedzie (brak mowy, porazka), wiec nie ma czego oceniac,
 *  - nie-oceniono: transkrypt jest, ale nikt nie zlecil oceny,
 *  - raport: jest zadanie oceny albo przebiegi (takze w toku i nieudane).
 */
export type RodzajStanuKarty = "czeka-na-podzial" | "czeka-na-etapy" | "brak-wypowiedzi" | "nie-oceniono" | "raport";

export interface UwagaZadania {
  /** porazka: zadanie sie poddalo. zapis: zadanie sie skonczylo, ale nie wszystkie wyniki sie zapisaly. */
  rodzaj: "porazka" | "zapis";
  tekst: string;
}

export interface RaportOceny {
  stan: RodzajStanuKarty;
  /** Czy ocena jeszcze trwa: zadanie czeka albo sie liczy. */
  trwa: boolean;
  /** Tylko gdy trwa: czy sesja jest juz w etapie oceny (liczy sie) czy jeszcze czeka na wczesniejsze etapy. */
  faza: "kolejka" | "liczy" | null;
  parametry: ParametrWidoku[];
  mowcy: MowcaRaportu[];
  uwagaZadania: UwagaZadania | null;
  /** Wersja metodyki, ktora policzyla przebiegi (z pierwszego przebiegu OK). */
  wersjaMetodyki: string | null;
  /** Modele, ktore policzyly przebiegi OK, bez powtorzen. */
  modele: string[];
}

// --- Wskazania i mowcy ---------------------------------------------------------------------------

/** Klucz "mowcy" dla wskazan, ktorych wypowiedzi nie ma (skasowana albo wskazanie bez numeru). */
export const KLUCZ_BEZ_MOWCY = "__bez-mowcy__";

export interface WskazanieWidoku {
  id: string;
  rodzaj: FindingKind;
  /** Indeks wypowiedzi (od 0, jak w bazie): do przewijania transkryptu. Null: wskazanie bez wypowiedzi. */
  indeks: number | null;
  /** Numer wypowiedzi (od 1) z shared/numeracja.ts: jedyny numer, ktorym karta i transkrypt sie rozumieja. */
  numer: number | null;
  cytat: string | null;
  cytatZweryfikowany: boolean;
  /** Cytat zweryfikowany, ale nie podswietlony w transkrypcie, bo nachodzi na inny. */
  nachodzi: boolean;
  /** Przy mocnej stronie: dlaczego to dobre. Przy do-poprawy: jak i czym mowca obniza wplyw. */
  uzasadnienie: string | null;
  /** Tylko mocna strona. */
  jakRozwijac: string | null;
  /** Tylko do-poprawy: 1-3 lepsze wersje. Bez wariantow od modelu - pusta lista. */
  warianty: WariantWskazaniaDto[];
}

export interface GrupaParametru {
  slug: string;
  tytul: string;
  wskazania: WskazanieWidoku[];
}

export interface CwiczenieWidoku {
  slug: string;
  tytul: string;
  tekst: string;
}

export interface MowcaRaportu {
  /** Speaker.id albo KLUCZ_BEZ_MOWCY. */
  klucz: string;
  nazwa: string;
  liczbaWypowiedzi: number;
  /** "Co wyszlo najlepiej": grupy w kolejnosci parametrow, tylko niepuste. */
  mocneStrony: GrupaParametru[];
  /** "Do poprawy": grupy w kolejnosci parametrow, tylko niepuste. */
  doPoprawy: GrupaParametru[];
  liczbaMocnychStron: number;
  liczbaDoPoprawy: number;
  /** Stale cwiczenia z metodyki dla parametrow, w ktorych TEN mowca ma rzeczy do poprawy. */
  cwiczenia: CwiczenieWidoku[];
}

/** Rodzaj wskazania z bazy; cokolwiek innego niz "mocna-strona" to "do-poprawy" (domyslna wartosc kolumny). */
function rodzajWskazania(f: Pick<LensFindingDto, "kind">): FindingKind {
  return f.kind === "mocna-strona" ? "mocna-strona" : "do-poprawy";
}

/** Warianty ze wskazania; dla starszych wierszy bez `variants` - jedyna propozycja z `suggestion`, bez uzasadnienia. */
export function wariantyWskazania(f: Pick<LensFindingDto, "variants" | "suggestion">): WariantWskazaniaDto[] {
  if (f.variants && f.variants.length > 0) return f.variants;
  if (f.suggestion && f.suggestion.trim().length > 0) return [{ tekst: f.suggestion, dlaczegoLepiej: "" }];
  return [];
}

// --- Podswietlenia i kolory w transkrypcie ------------------------------------------------------

/**
 * Uwaga przy wypowiedzi: to samo wskazanie, ktore karta oceny pokazuje per mowca, ale ulozone przy dymku, z ktorego
 * pochodzi. Dane sa te same co w karcie (jedno zrodlo prawdy), zmienia sie tylko miejsce.
 */
export interface UwagaWypowiedzi {
  /** Id wskazania. */
  id: string;
  /**
   * Litera przy podkreslonym cytacie ("a", "b", ...), po ktorej widac, ktore podkreslenie jest ktora uwaga.
   * Null, gdy cytatu nie ma w transkrypcie (niezweryfikowany, poza tekstem albo nachodzi na inny).
   * Litera, nie cyfra: jedynym numerem w interfejsie jest numer wypowiedzi.
   */
  znacznik: string | null;
  rodzaj: FindingKind;
  /** Tytul parametru po polsku. */
  parametr: string;
  cytat: string | null;
  cytatZweryfikowany: boolean;
  /** Cytat zweryfikowany, ale niepodkreslony, bo nachodzi na inny. */
  nachodzi: boolean;
  /** Przy mocnej stronie: dlaczego to dobre. Przy do-poprawy: jak i czym mowca obniza wplyw. */
  uzasadnienie: string | null;
  /** Tylko mocna strona. */
  jakRozwijac: string | null;
  /** Tylko do-poprawy, 1-3. Bez wariantow od modelu - pusta lista. */
  warianty: WariantWskazaniaDto[];
}

/** Litera uwagi po pozycji podkreslenia w wypowiedzi: a, b, ..., z, aa, ab, ... */
export function znacznikUwagi(pozycja: number): string {
  let n = pozycja;
  let litery = "";
  do {
    litery = String.fromCharCode(97 + (n % 26)) + litery;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return litery;
}

export interface PodswietleniaSesji {
  /** Zakresy do narysowania w wypowiedziach, po indeksie wypowiedzi. Bez nakladania. */
  zakresy: Map<number, ZakresCytatu[]>;
  /** Id wskazan, ktorych cytat jest w transkrypcie widoczny. */
  widoczne: Set<string>;
  /** Jakosc (kolor) wypowiedzi. Wypowiedz bez zweryfikowanego wskazania nie ma wpisu. */
  jakosci: Map<number, JakoscWypowiedzi>;
  /**
   * Uwagi przy wypowiedziach, po indeksie wypowiedzi: najpierw te z podkreslonym cytatem (w kolejnosci
   * podkreslen w tekscie), potem reszta. Wypowiedz bez wskazan nie ma wpisu.
   */
  uwagi: Map<number, UwagaWypowiedzi[]>;
}

/** Wskazanie z jednego przebiegu, zanim wiadomo, czy jego cytat jest w transkrypcie. */
interface WskazanieWypowiedzi {
  finding: LensFindingDto;
  parametr: string;
  rodzaj: FindingKind;
  zakres: { start: number; koniec: number } | null;
}

/**
 * Podswietlenia cytatow, jakosc wypowiedzi i uwagi z przebiegow oceny.
 *
 * Wskazanie trafia do transkryptu WYLACZNIE, gdy cytat zostal zweryfikowany w kodzie i miesci sie w tekscie
 * wypowiedzi (zakresWskazania). Wskazanie, ktorego cytatu w tekscie nie ma, nie barwi tez wypowiedzi:
 * kolor jest dowodem, a dowodu, ktorego nie ma, nie pokazujemy. Cytat, ktory nachodzi na inny, barwi wypowiedz
 * (bo jest zweryfikowany), ale nie jest podkreslany - widoczne zostaje tylko jedno podkreslenie.
 *
 * Uwaga przy dymku powstaje dla KAZDEGO wskazania z wypowiedzia, takze dla niezweryfikowanego: tam niesie
 * jawna plakietke "cytat niezweryfikowany" i nie ma litery, bo nie ma czego podkreslic.
 */
export function policzPodswietlenia(
  wypowiedzi: readonly Pick<UtteranceDto, "index" | "text">[],
  przebiegi: readonly Pick<LensRunDto, "findings" | "lensTitle" | "lensSlug">[],
): PodswietleniaSesji {
  const dlugosci = new Map(wypowiedzi.map((u) => [u.index, u.text.length]));
  const kandydaci = new Map<number, ZakresCytatu[]>();
  const wskazaniaWgWypowiedzi = new Map<number, WskazanieWypowiedzi[]>();
  const doKoloru: { utteranceIndex: number; kind: FindingKind; quoteVerified: boolean }[] = [];

  for (const przebieg of przebiegi) {
    const parametr = przebieg.lensTitle.trim().length > 0 ? przebieg.lensTitle : przebieg.lensSlug;
    for (const f of [...przebieg.findings].sort((a, b) => a.orderIndex - b.orderIndex)) {
      if (f.utteranceIndex === null) continue;
      const dlugosc = dlugosci.get(f.utteranceIndex);
      if (dlugosc === undefined) continue;

      const rodzaj = rodzajWskazania(f);
      const zakres = zakresWskazania(f, dlugosc);
      doKoloru.push({ utteranceIndex: f.utteranceIndex, kind: rodzaj, quoteVerified: zakres !== null });

      const wszystkie = wskazaniaWgWypowiedzi.get(f.utteranceIndex) ?? [];
      wszystkie.push({ finding: f, parametr, rodzaj, zakres });
      wskazaniaWgWypowiedzi.set(f.utteranceIndex, wszystkie);

      if (zakres === null) continue;

      const lista = kandydaci.get(f.utteranceIndex) ?? [];
      lista.push({ id: f.id, rodzaj, ...zakres });
      kandydaci.set(f.utteranceIndex, lista);
    }
  }

  const zakresy = new Map<number, ZakresCytatu[]>();
  const widoczne = new Set<string>();
  /** Pozycja kazdego narysowanego zakresu w wypowiedzi: z niej bierze sie litera uwagi. */
  const pozycjeZakresow = new Map<number, Map<string, number>>();
  for (const [indeks, lista] of kandydaci) {
    const wynik = rozstrzygnijNakladanie(lista, dlugosci.get(indeks) ?? 0);
    zakresy.set(indeks, wynik.rysowane);
    for (const id of wynik.widoczne) widoczne.add(id);
    pozycjeZakresow.set(indeks, new Map(wynik.rysowane.map((z, i) => [`${z.start}:${z.koniec}`, i])));
  }

  const uwagi = new Map<number, UwagaWypowiedzi[]>();
  for (const [indeks, wskazania] of wskazaniaWgWypowiedzi) {
    const pozycje = pozycjeZakresow.get(indeks);
    const uporzadkowane = wskazania
      .map((w, kolejnosc) => {
        const widoczny = w.zakres !== null && widoczne.has(w.finding.id);
        const pozycja = widoczny && w.zakres ? (pozycje?.get(`${w.zakres.start}:${w.zakres.koniec}`) ?? null) : null;
        const uwaga: UwagaWypowiedzi = {
          id: w.finding.id,
          znacznik: pozycja === null ? null : znacznikUwagi(pozycja),
          rodzaj: w.rodzaj,
          parametr: w.parametr,
          cytat: w.finding.quote,
          cytatZweryfikowany: w.finding.quoteVerified,
          nachodzi: w.zakres !== null && !widoczny,
          uzasadnienie: w.finding.rationale,
          jakRozwijac: w.rodzaj === "mocna-strona" ? w.finding.howToDevelop : null,
          warianty: w.rodzaj === "do-poprawy" ? wariantyWskazania(w.finding) : [],
        };
        return { uwaga, pozycja, kolejnosc };
      })
      .sort((a, b) => (a.pozycja ?? Infinity) - (b.pozycja ?? Infinity) || a.kolejnosc - b.kolejnosc);
    uwagi.set(
      indeks,
      uporzadkowane.map((u) => u.uwaga),
    );
  }

  return { zakresy, widoczne, jakosci: koloryWypowiedzi(doKoloru), uwagi };
}

// --- Skladanie raportu --------------------------------------------------------------------------

/** Najmniejszy ksztalt sesji, z ktorego sklada sie raport (SessionDetail go spelnia). */
export type SesjaDoRaportu = Pick<
  SessionDetail,
  "status" | "ocena" | "lensRuns" | "speakers" | "utterances" | "methodologyVersion"
>;

function najnowszyPrzebieg(przebiegi: readonly LensRunDto[], slug: string): LensRunDto | null {
  let najnowszy: LensRunDto | null = null;
  for (const p of przebiegi) {
    if (p.lensSlug !== slug) continue;
    if (najnowszy === null || p.createdAt > najnowszy.createdAt) najnowszy = p;
  }
  return najnowszy;
}

/** Wybrane parametry w kolejnosci wyboru zadania, a po nich przebiegi, ktorych wybor nie wymienia (nie powinno ich byc). */
function zbudujParametry(sesja: SesjaDoRaportu, trwa: boolean): ParametrWidoku[] {
  const wybor = (sesja.ocena?.soczewki ?? []).map((s) => ({ slug: s.slug, tytul: s.title }));
  const znane = new Set(wybor.map((s) => s.slug));
  for (const p of sesja.lensRuns) {
    if (znane.has(p.lensSlug)) continue;
    znane.add(p.lensSlug);
    wybor.push({ slug: p.lensSlug, tytul: p.lensTitle });
  }

  return wybor.map(({ slug, tytul }) => {
    const przebieg = najnowszyPrzebieg(sesja.lensRuns, slug);
    return {
      slug,
      tytul: przebieg !== null && przebieg.lensTitle.trim().length > 0 ? przebieg.lensTitle : tytul || slug,
      stan: stanParametru(przebieg, trwa),
      przebieg,
    };
  });
}

function zbudujMowcow(
  sesja: SesjaDoRaportu,
  parametry: readonly ParametrWidoku[],
  widoczne: ReadonlySet<string>,
): MowcaRaportu[] {
  const wypowiedzi = new Map(sesja.utterances.map((u) => [u.index, u]));
  const znaniMowcy = new Set(sesja.speakers.map((m) => m.id));

  /** Dla kazdego mowcy i parametru: mocne strony i rzeczy do poprawy. */
  const wgMowcy = new Map<string, Map<string, { mocne: WskazanieWidoku[]; poprawy: WskazanieWidoku[] }>>();

  for (const parametr of parametry) {
    if (parametr.przebieg === null) continue;
    for (const f of [...parametr.przebieg.findings].sort((a, b) => a.orderIndex - b.orderIndex)) {
      const wypowiedz = f.utteranceIndex === null ? undefined : wypowiedzi.get(f.utteranceIndex);
      const klucz = wypowiedz && znaniMowcy.has(wypowiedz.speakerId) ? wypowiedz.speakerId : KLUCZ_BEZ_MOWCY;
      const rodzaj = rodzajWskazania(f);
      const dlugosc = wypowiedz?.text.length ?? 0;

      const wskazanie: WskazanieWidoku = {
        id: f.id,
        rodzaj,
        indeks: wypowiedz ? wypowiedz.index : null,
        numer: wypowiedz ? numerWypowiedzi(wypowiedz.index) : null,
        cytat: f.quote,
        cytatZweryfikowany: f.quoteVerified,
        nachodzi: zakresWskazania(f, dlugosc) !== null && !widoczne.has(f.id),
        uzasadnienie: f.rationale,
        jakRozwijac: rodzaj === "mocna-strona" ? f.howToDevelop : null,
        warianty: rodzaj === "do-poprawy" ? wariantyWskazania(f) : [],
      };

      const mapaMowcy = wgMowcy.get(klucz) ?? new Map();
      const wpis = mapaMowcy.get(parametr.slug) ?? { mocne: [], poprawy: [] };
      (rodzaj === "mocna-strona" ? wpis.mocne : wpis.poprawy).push(wskazanie);
      mapaMowcy.set(parametr.slug, wpis);
      wgMowcy.set(klucz, mapaMowcy);
    }
  }

  const zlozMowce = (klucz: string, nazwa: string, liczbaWypowiedzi: number): MowcaRaportu => {
    const mapa = wgMowcy.get(klucz);
    const mocneStrony: GrupaParametru[] = [];
    const doPoprawy: GrupaParametru[] = [];
    const cwiczenia: CwiczenieWidoku[] = [];

    for (const parametr of parametry) {
      const wpis = mapa?.get(parametr.slug);
      if (!wpis) continue;
      if (wpis.mocne.length > 0) mocneStrony.push({ slug: parametr.slug, tytul: parametr.tytul, wskazania: wpis.mocne });
      if (wpis.poprawy.length > 0) {
        doPoprawy.push({ slug: parametr.slug, tytul: parametr.tytul, wskazania: wpis.poprawy });
        // Cwiczenie to staly tekst z metodyki dla parametru, w ktorym TEN mowca ma cos do poprawy. API zwraca je
        // tylko wtedy, gdy przebieg ma wskazania do poprawy i wersja metodyki jest znana.
        const cwiczenie = parametr.przebieg?.exercise;
        if (cwiczenie && cwiczenie.trim().length > 0) {
          cwiczenia.push({ slug: parametr.slug, tytul: parametr.tytul, tekst: cwiczenie });
        }
      }
    }

    return {
      klucz,
      nazwa,
      liczbaWypowiedzi,
      mocneStrony,
      doPoprawy,
      liczbaMocnychStron: mocneStrony.reduce((suma, g) => suma + g.wskazania.length, 0),
      liczbaDoPoprawy: doPoprawy.reduce((suma, g) => suma + g.wskazania.length, 0),
      cwiczenia,
    };
  };

  // Lista mowcow z session.speakers, nie z summary.speakers: summary zna tylko tych, ktorzy mowili.
  const wynik = sesja.speakers.map((m) =>
    zlozMowce(m.id, m.displayName ?? m.label, sesja.utterances.filter((u) => u.speakerId === m.id).length),
  );
  if (wgMowcy.has(KLUCZ_BEZ_MOWCY)) wynik.push(zlozMowce(KLUCZ_BEZ_MOWCY, "Bez przypisanej wypowiedzi", 0));
  return wynik;
}

/** Mowca pokazany na starcie: pierwszy, ktory ma jakiekolwiek wskazania, a gdy zaden nie ma - pierwszy. */
export function domyslnyMowca(mowcy: readonly MowcaRaportu[]): string | null {
  const zWskazaniami = mowcy.find((m) => m.liczbaMocnychStron + m.liczbaDoPoprawy > 0);
  return (zWskazaniami ?? mowcy[0])?.klucz ?? null;
}

const PUSTY: Omit<RaportOceny, "stan"> = {
  trwa: false,
  faza: null,
  parametry: [],
  mowcy: [],
  uwagaZadania: null,
  wersjaMetodyki: null,
  modele: [],
};

export function zbudujRaport(sesja: SesjaDoRaportu): RaportOceny {
  if (sesja.status === STATUS_CZEKA_NA_CZLOWIEKA) return { ...PUSTY, stan: "czeka-na-podzial" };

  if (sesja.utterances.length === 0) {
    return { ...PUSTY, stan: IN_PROGRESS.has(sesja.status) ? "czeka-na-etapy" : "brak-wypowiedzi" };
  }

  if (sesja.ocena === null && sesja.lensRuns.length === 0) return { ...PUSTY, stan: "nie-oceniono" };

  const statusZadania = sesja.ocena?.status ?? null;
  // O tym, czy ocena trwa, rozstrzyga STATUS SESJI, a nie status zadania. Worker konczy sesje (DONE) chwile
  // PRZED zadaniem (DONE), a widok przestaje odpytywac serwer na DONE: gdyby "trwa" brano tez z zadania, odpowiedz
  // zlapana w tej szczelinie zostawilaby karte na zawsze w "Liczy sie". Wyniki sa wtedy juz zapisane.
  const trwa = IN_PROGRESS.has(sesja.status);
  const parametry = zbudujParametry(sesja, trwa);
  const { widoczne } = policzPodswietlenia(sesja.utterances, sesja.lensRuns);

  let uwagaZadania: UwagaZadania | null = null;
  if (sesja.ocena?.error) {
    if (statusZadania === "FAILED") uwagaZadania = { rodzaj: "porazka", tekst: sesja.ocena.error };
    else if (statusZadania === "DONE") uwagaZadania = { rodzaj: "zapis", tekst: sesja.ocena.error };
  }

  const przebiegiOk = parametry.map((p) => p.przebieg).filter((p): p is LensRunDto => p !== null && p.status === "OK");

  return {
    stan: "raport",
    trwa,
    faza: trwa ? (sesja.status === "LENSES" ? "liczy" : "kolejka") : null,
    parametry,
    mowcy: zbudujMowcow(sesja, parametry, widoczne),
    uwagaZadania,
    wersjaMetodyki: przebiegiOk[0]?.methodologyVersion ?? sesja.methodologyVersion,
    modele: [...new Set(przebiegiOk.map((p) => p.model).filter((m) => m.length > 0))],
  };
}

// --- Co wolno zrobic z ocena --------------------------------------------------------------------

export type Mozliwosc = { mozna: true } | { mozna: false; powod: string };

/**
 * Czy wolno zlecic ocene (pierwsza albo ponowna). Serwer odmawia tego samego kodem 409, ale przycisk wygaszony
 * z powodem (S-10) jest uczciwszy niz klikniecie, ktore konczy sie bledem. Dozwolone: DONE oraz FAILED z transkryptem.
 */
export function czyMoznaOcenic(status: string, maWypowiedzi: boolean): Mozliwosc {
  if (status === STATUS_CZEKA_NA_CZLOWIEKA) {
    return { mozna: false, powod: "Najpierw zatwierdź podział tekstu na wypowiedzi, wtedy ocena ruszy sama." };
  }
  if (IN_PROGRESS.has(status)) {
    return { mozna: false, powod: "Ta analiza jest jeszcze przetwarzana. Poczekaj, aż się skończy." };
  }
  if (!maWypowiedzi) {
    return { mozna: false, powod: "Ta analiza nie ma transkryptu, więc nie ma czego oceniać." };
  }
  return { mozna: true };
}

/** Czy da sie pobrac raport (Word albo PDF): serwer odmawia w trakcie pracy, przy czekaniu na podzial i bez zadnego przebiegu. */
export function czyMoznaPobracRaport(status: string, liczbaPrzebiegow: number): Mozliwosc {
  if (status === STATUS_CZEKA_NA_CZLOWIEKA) {
    return { mozna: false, powod: "Raport powstanie po zatwierdzeniu podziału i ocenie." };
  }
  if (IN_PROGRESS.has(status)) {
    return { mozna: false, powod: "Raport będzie gotowy, gdy skończy się ocena." };
  }
  if (liczbaPrzebiegow === 0) {
    return { mozna: false, powod: "Nie ma jeszcze wyników oceny, więc nie ma czego pobrać." };
  }
  return { mozna: true };
}
